import { QueryClient } from '@tanstack/react-query';
import { isClientError, isTransientClientError } from 'src/api/Http/http-error-kind.logic';
import { isCancelError } from 'src/api/Http/Http.types';

/**
 * API Data Caching Strategy Configuration
 *
 * This configuration implements smart caching to reduce unnecessary network requests
 * and improve user experience across the application.
 *
 * CACHE TIMES:
 * - staleTime (30s): Data is considered fresh for 30 seconds. During this time,
 *   navigating between pages won't trigger new API calls for cached data.
 * - cacheTime (5min): Unused cache entries are garbage collected after 5 minutes.
 *
 * RETRY STRATEGY:
 * - Failed requests retry up to 3 times with exponential backoff
 * - Delay: 1s → 2s → 4s (capped at 30s)
 * - Network errors and 5xx errors are retried; 4xx client errors are not, except the
 *   transient ones (408/425/429), which are
 *
 * BACKGROUND REFRESH:
 * - refetchOnWindowFocus: DISABLED - causes aggressive API storms in mobile
 *   WebViews where every app switch triggers focus events, leading to UI
 *   flicker, loading states, and unexpected navigation (e.g. "Create Request").
 *   Data freshness is handled by staleTime + refetchOnMount instead.
 * - refetchOnReconnect: Refreshes stale data when network reconnects
 *
 * TESTING AREAS:
 * - Dashboard: Navigate away and back, data should load instantly
 * - Scripts List: Refresh page, check network tab for cached responses
 * - Media Library: Switch between folders, verify no duplicate API calls
 * - Brand Selector: Change brands, ensure proper cache invalidation
 * - Offline Mode: Disconnect internet, verify cached data still displays
 */

/** Default stale time for queries (30 seconds) */
export const DEFAULT_STALE_TIME = 30 * 1000;

/** Default cache time for unused cache entries (5 minutes) - called cacheTime in v4 */
export const DEFAULT_CACHE_TIME = 5 * 60 * 1000;

/** Maximum retry delay in milliseconds */
const MAX_RETRY_DELAY = 30 * 1000;

/**
 * Determines if a failed request should be retried based on the error type.
 * - Retries network errors and server errors (5xx)
 * - Does NOT retry client errors (4xx) as they indicate invalid requests, except
 *   408/425/429, which are transient and are retried
 */
const shouldRetry = (failureCount: number, error: unknown): boolean => {
  // Max 3 retries
  if (failureCount >= 3) return false;

  // Don't retry cancelled requests (e.g., user navigated away)
  if (isCancelError(error)) return false;

  // Don't retry 4xx: a client error fails identically if repeated.
  //
  // Reads HttpError.code via isClientError. This used to read `.status ?? .response.status`,
  // neither of which exists on HttpError (errorInterceptor flattens the axios shape), so the
  // guard never fired and every 4xx WAS retried three times — the opposite of what the
  // docblock above promises, and ~7s of pointless backoff (1s+2s+4s) on each one.
  //
  // 408/425/429 are exempt: those are the 4xx that DO succeed on repeat, and backing off is
  // the specified response to them. See isTransientClientError — the previously-broken guard
  // was accidentally retrying them, so excluding the whole range would have regressed
  // rate-limit handling while fixing the backoff waste.
  if (isClientError(error) && !isTransientClientError(error)) return false;

  return true;
};

/**
 * Calculates retry delay with exponential backoff.
 * Delay doubles with each attempt: 1s → 2s → 4s, capped at 30s
 */
const getRetryDelay = (attemptIndex: number): number => {
  const delay = Math.min(1000 * Math.pow(2, attemptIndex), MAX_RETRY_DELAY);
  return delay;
};

const reactQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache Configuration
      staleTime: DEFAULT_STALE_TIME,
      cacheTime: DEFAULT_CACHE_TIME,

      // Retry Configuration
      retry: shouldRetry,
      retryDelay: getRetryDelay,

      // Background Refresh Strategy
      // Disabled: refetchOnWindowFocus causes aggressive API storms when loaded
      // inside mobile WebViews (every app switch triggers focus change) and can
      // cause WebView remounts leading to unexpected "Create Request" redirects.
      // Data freshness is already handled by staleTime + refetchOnMount.
      refetchOnWindowFocus: false,
      refetchOnReconnect: 'always',
      refetchOnMount: true,

      // Structural sharing for better performance with large datasets
      structuralSharing: true,
    },
    mutations: {
      // Mutations should not retry by default (user actions)
      retry: false,
    },
  },
});

export { reactQueryClient };
